import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function AccountSettings() {
  const navigate = useNavigate();

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('therapylist_user') || 'null');
    } catch {
      return null;
    }
  })();

  const [accountEmail, setAccountEmail] = useState(storedUser?.email || '');

  // Change email state
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailError('');
    setEmailSuccess('');

    if (!newEmail.trim() || !emailPassword) {
      setEmailError('Please enter your new email and current password.');
      return;
    }

    setIsChangingEmail(true);
    try {
      const token = localStorage.getItem('therapylist_token');
      const response = await fetch(`${API_BASE_URL}/api/auth/change-email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          newEmail: newEmail.trim(),
          currentPassword: emailPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setEmailError(data.errors?.[0]?.msg || data.message || 'Failed to update email.');
        return;
      }

      // Keep the cached user in sync so the dashboard shows the new email
      const updatedUser = { ...(storedUser || {}), email: data.user.email };
      localStorage.setItem('therapylist_user', JSON.stringify(updatedUser));
      setAccountEmail(data.user.email);
      setNewEmail('');
      setEmailPassword('');
      setEmailSuccess('Your login email has been updated. Use it the next time you sign in.');
    } catch (error) {
      console.error('Change email error:', error);
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');

    if (!deletePassword) {
      setDeleteError('Please enter your password.');
      return;
    }
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm.');
      return;
    }

    setIsDeleting(true);
    try {
      const token = localStorage.getItem('therapylist_token');
      const response = await fetch(`${API_BASE_URL}/api/auth/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: deletePassword })
      });

      const data = await response.json();

      if (!response.ok) {
        setDeleteError(data.errors?.[0]?.msg || data.message || 'Failed to delete account.');
        return;
      }

      localStorage.removeItem('therapylist_token');
      localStorage.removeItem('therapylist_user');
      localStorage.removeItem('token');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Delete account error:', error);
      setDeleteError('Something went wrong. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeleteConfirmText('');
    setDeleteError('');
  };

  return (
    <div className="space-y-6">
      {/* Login Email */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-[#254e70]/10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#254e70] rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-medium text-[#254e70]" style={{ fontFamily: 'Kanit' }}>Login Email</h3>
            <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>
              The email address you use to sign in to TherapyList
            </p>
          </div>
        </div>

        <div className="mb-6 p-4 bg-[#e4fde1] rounded-lg border border-[#254e70]/10">
          <span className="text-sm text-gray-600 block mb-1" style={{ fontFamily: 'Nunito Sans' }}>Current email</span>
          <span className="font-medium text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>{accountEmail || '—'}</span>
        </div>

        <form onSubmit={handleChangeEmail} className="space-y-4 max-w-lg">
          <div>
            <label htmlFor="new-email" className="block text-sm font-medium text-[#254e70] mb-1" style={{ fontFamily: 'Nunito Sans' }}>
              New email address
            </label>
            <input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="w-full px-4 py-2 border border-[#254e70]/20 rounded-lg focus:ring-2 focus:ring-[#9dacff] focus:border-[#254e70] transition-all duration-200"
              style={{ fontFamily: 'Nunito Sans' }}
            />
          </div>
          <div>
            <label htmlFor="email-current-password" className="block text-sm font-medium text-[#254e70] mb-1" style={{ fontFamily: 'Nunito Sans' }}>
              Current password
            </label>
            <input
              id="email-current-password"
              type="password"
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
              placeholder="Enter your password to confirm"
              autoComplete="current-password"
              className="w-full px-4 py-2 border border-[#254e70]/20 rounded-lg focus:ring-2 focus:ring-[#9dacff] focus:border-[#254e70] transition-all duration-200"
              style={{ fontFamily: 'Nunito Sans' }}
            />
          </div>

          {emailError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" style={{ fontFamily: 'Nunito Sans' }}>
              {emailError}
            </div>
          )}
          {emailSuccess && (
            <div className="p-3 bg-[#e4fde1] border border-[#254e70]/20 rounded-lg text-sm text-[#254e70]" style={{ fontFamily: 'Nunito Sans' }}>
              {emailSuccess}
            </div>
          )}

          <button
            type="submit"
            disabled={isChangingEmail}
            className="px-5 py-2 bg-[#254e70] text-white rounded-full text-sm font-medium hover:bg-[#1d3e5a] transition-all duration-200 disabled:opacity-50 shadow-sm"
            style={{ fontFamily: 'Nunito Sans' }}
          >
            {isChangingEmail ? 'Updating...' : 'Update Email'}
          </button>
        </form>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-red-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-medium text-red-700" style={{ fontFamily: 'Kanit' }}>Delete Account</h3>
            <p className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans' }}>
              Permanently delete your account and all of your data
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4 max-w-2xl" style={{ fontFamily: 'Nunito Sans' }}>
          This will permanently remove your profile from TherapyList and delete your client
          inquiries and analytics. This action cannot be undone.
        </p>

        <button
          onClick={() => setShowDeleteModal(true)}
          className="px-5 py-2 bg-white border border-red-300 text-red-600 rounded-full text-sm font-medium hover:bg-red-50 transition-all duration-200"
          style={{ fontFamily: 'Nunito Sans' }}
        >
          Delete My Account
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeDeleteModal}></div>
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6" role="dialog" aria-modal="true" aria-labelledby="delete-account-title">
            <h4 id="delete-account-title" className="text-xl font-medium text-red-700 mb-2" style={{ fontFamily: 'Kanit' }}>
              Delete your account?
            </h4>
            <p className="text-sm text-gray-600 mb-4" style={{ fontFamily: 'Nunito Sans' }}>
              This will permanently delete:
            </p>
            <ul className="text-sm text-gray-600 mb-4 space-y-1 list-disc list-inside" style={{ fontFamily: 'Nunito Sans' }}>
              <li>Your therapist profile and public listing</li>
              <li>All client inquiries and message history</li>
              <li>Your analytics and contact data</li>
              <li>Your login credentials</li>
            </ul>

            <form onSubmit={handleDeleteAccount} className="space-y-4">
              <div>
                <label htmlFor="delete-password" className="block text-sm font-medium text-[#254e70] mb-1" style={{ fontFamily: 'Nunito Sans' }}>
                  Your password
                </label>
                <input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full px-4 py-2 border border-[#254e70]/20 rounded-lg focus:ring-2 focus:ring-red-300 focus:border-red-400 transition-all duration-200"
                  style={{ fontFamily: 'Nunito Sans' }}
                />
              </div>
              <div>
                <label htmlFor="delete-confirm" className="block text-sm font-medium text-[#254e70] mb-1" style={{ fontFamily: 'Nunito Sans' }}>
                  Type <span className="font-bold">DELETE</span> to confirm
                </label>
                <input
                  id="delete-confirm"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                  className="w-full px-4 py-2 border border-[#254e70]/20 rounded-lg focus:ring-2 focus:ring-red-300 focus:border-red-400 transition-all duration-200"
                  style={{ fontFamily: 'Nunito Sans' }}
                />
              </div>

              {deleteError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" style={{ fontFamily: 'Nunito Sans' }}>
                  {deleteError}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-[#254e70]/70 hover:text-[#254e70] transition-colors duration-200 disabled:opacity-50"
                  style={{ fontFamily: 'Nunito Sans' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isDeleting || deleteConfirmText.trim().toUpperCase() !== 'DELETE' || !deletePassword}
                  className="px-5 py-2 bg-red-600 text-white rounded-full text-sm font-medium hover:bg-red-700 transition-all duration-200 disabled:opacity-50 shadow-sm"
                  style={{ fontFamily: 'Nunito Sans' }}
                >
                  {isDeleting ? 'Deleting...' : 'Permanently Delete'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountSettings;
